import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Button,
  Input,
  Popconfirm,
  message,
  Card,
  Typography,
  Divider,
  Empty,
  Spin,
  Row,
  Col,
  Tag,
} from 'antd';
import {
  DeleteOutlined,
  PlusOutlined,
  PictureOutlined,
  LinkOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Text } = Typography;

const API_URL = process.env.REACT_APP_API_URL;
const MAX_IMAGES = 20;

// Convert a Google Drive share link to a thumbnail URL we can render in <img>.
// Supports formats like:
//   https://drive.google.com/file/d/FILE_ID/view?usp=sharing
//   https://drive.google.com/open?id=FILE_ID
//   https://drive.google.com/uc?id=FILE_ID
const extractDriveFileId = (link) => {
  if (!link) return null;
  const patterns = [
    /\/file\/d\/([a-zA-Z0-9_-]+)/,
    /[?&]id=([a-zA-Z0-9_-]+)/,
  ];
  for (const re of patterns) {
    const match = link.match(re);
    if (match && match[1]) return match[1];
  }
  return null;
};

const getDrivePreviewUrls = (link) => {
  const id = extractDriveFileId(link);
  if (!id) return [];
  return [
    `https://lh3.googleusercontent.com/d/${id}=w600`,
    `https://drive.google.com/thumbnail?id=${id}&sz=w600`,
  ];
};

const StudentBookImagesModal = ({ visible, onClose, studentId, studentName, onChange }) => {
  const [images, setImages] = useState([]);
  const [newLink, setNewLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const token = localStorage.getItem('token');

  const fetchImages = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/student-book-images/${studentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setImages(res.data);
    } catch (err) {
      console.error('Failed to fetch book images:', err);
      message.error('Error loading book images');
    } finally {
      setLoading(false);
    }
  }, [studentId, token]);

  const handleAddImage = useCallback(async () => {
    const trimmed = newLink.trim();
    if (!trimmed) {
      message.warning('Please paste a Google Drive image link');
      return;
    }
    if (images.length >= MAX_IMAGES) {
      message.warning(`Maximum of ${MAX_IMAGES} images per student`);
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/student-book-images`,
        { student_id: studentId, image_link: trimmed },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      message.success('Image link added');
      setNewLink('');
      fetchImages();
      if (onChange) onChange();
    } catch (err) {
      console.error('Failed to add book image:', err);
      message.error(err.response?.data?.message || 'Error adding image');
    } finally {
      setSubmitting(false);
    }
  }, [newLink, studentId, token, images.length, fetchImages, onChange]);

  const handleDeleteImage = useCallback(
    async (id) => {
      try {
        await axios.delete(`${API_URL}/student-book-images/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        message.success('Image deleted');
        fetchImages();
        if (onChange) onChange();
      } catch (err) {
        console.error('Failed to delete book image:', err);
        message.error('Error deleting image');
      }
    },
    [token, fetchImages, onChange],
  );

  useEffect(() => {
    if (visible) fetchImages();
  }, [visible, fetchImages]);

  const renderImageCard = (item) => {
    const previewUrls = getDrivePreviewUrls(item.image_link);
    return (
      <Col xs={24} sm={12} md={8} key={item.id}>
        <Card
          size="small"
          bordered
          style={{ marginBottom: 16 }}
          cover={
            <div
              style={{
                height: 180,
                background: '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              {previewUrls.length > 0 ? (
                <a href={item.image_link} target="_blank" rel="noopener noreferrer">
                  <img
                    src={previewUrls[0]}
                    data-fallback-index="0"
                    alt="Book"
                    referrerPolicy="no-referrer"
                    style={{ maxHeight: 180, maxWidth: '100%', objectFit: 'contain' }}
                    onError={(e) => {
                      const idx = parseInt(e.target.dataset.fallbackIndex, 10);
                      const next = idx + 1;
                      if (next < previewUrls.length) {
                        e.target.dataset.fallbackIndex = String(next);
                        e.target.src = previewUrls[next];
                      } else {
                        e.target.style.display = 'none';
                      }
                    }}
                  />
                </a>
              ) : (
                <PictureOutlined style={{ fontSize: 48, color: '#bfbfbf' }} />
              )}
            </div>
          }
          actions={[
            <a
              key="open"
              href={item.image_link}
              target="_blank"
              rel="noopener noreferrer"
            >
              <LinkOutlined /> Open
            </a>,
            <Popconfirm
              key="delete"
              title="Delete this image?"
              onConfirm={() => handleDeleteImage(item.id)}
              okText="Yes"
              cancelText="No"
              okButtonProps={{ danger: true }}
            >
              <Button type="text" danger icon={<DeleteOutlined />} size="small">
                Delete
              </Button>
            </Popconfirm>,
          ]}
        >
          <Text type="secondary" style={{ fontSize: 12 }}>
            <ClockCircleOutlined style={{ marginRight: 4 }} />
            {dayjs(item.created_at).format('DD MMM YY, HH:mm')}
            {item.created_by_name ? ` · ${item.created_by_name}` : ''}
          </Text>
        </Card>
      </Col>
    );
  };

  const limitReached = images.length >= MAX_IMAGES;

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <PictureOutlined style={{ marginRight: 8, color: '#1890ff' }} />
          <span>Book Images for {studentName || 'Student'}</span>
          <Tag
            color={limitReached ? 'red' : 'blue'}
            style={{ marginLeft: 12 }}
          >
            {images.length} / {MAX_IMAGES}
          </Tag>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={900}
    >
      <Card bordered={false} style={{ marginBottom: 24, background: '#f9f9f9' }}>
        <Input.Search
          value={newLink}
          onChange={(e) => setNewLink(e.target.value)}
          onSearch={handleAddImage}
          placeholder="Paste a Google Drive share link (https://drive.google.com/file/d/.../view)"
          enterButton={
            <Button type="primary" icon={<PlusOutlined />} loading={submitting}>
              Add Image
            </Button>
          }
          size="large"
          disabled={limitReached}
        />
        {limitReached && (
          <Text type="danger" style={{ display: 'block', marginTop: 8 }}>
            Limit reached. Delete an image to add a new one.
          </Text>
        )}
      </Card>

      <Divider orientation="left">
        <Text strong>
          {images.length} Image{images.length !== 1 ? 's' : ''}
        </Text>
      </Divider>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <Spin tip="Loading images..." />
        </div>
      ) : images.length > 0 ? (
        <Row gutter={16}>{images.map(renderImageCard)}</Row>
      ) : (
        <Empty
          description="No book images yet"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ margin: '24px 0' }}
        />
      )}
    </Modal>
  );
};

export default StudentBookImagesModal;
